/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import LocationContentLink from "@insite/content-library/Components/LocationContentLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import Phone from "@insite/mobius/Icons/Phone";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    locationFinderUrl: getPageLinkByPageType(state, "LocationFinderPage")?.url,
});

type Props = ReturnType<typeof mapStateToProps> & HasHistory;

export interface DealerInformationStyles {
    container?: GridContainerProps;
    selectedDealerDisplayBackToResultsGridItem?: GridItemProps;
    selectedDealerDisplayBackToResultsLink?: LinkPresentationProps;
    nameGridItem?: GridItemProps;
    nameText?: TypographyPresentationProps;
    addressInfoGridItem?: GridItemProps;
    addressInfoIcon?: IconPresentationProps;
    addressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    phoneGridItem?: GridItemProps;
    phoneLink?: LinkPresentationProps;
    distanceGridItem?: GridItemProps;
    distanceDisplayText?: TypographyPresentationProps;
    htmlContentGridItem?: GridItemProps;
    hoursContentGridItem?: GridItemProps;
    googleMapsDirectionLinkGridItem?: GridItemProps;
    googleMapsDirectionLink?: LinkPresentationProps;
    visitWebsiteLinkGridItem?: GridItemProps;
    visitWebsiteLink?: LinkPresentationProps;
    locationContentModal?: ModalPresentationProps;
}

export const dealerInformationStyles: DealerInformationStyles = {
    container: {
        gap: 10,
    },
    selectedDealerDisplayBackToResultsGridItem: {
        width: 12,
    },
    selectedDealerDisplayBackToResultsLink: {
        icon: {
            iconProps: {
                src: ChevronLeft,
            },
            position: "left",
        },
    },
    nameGridItem: {
        width: 12,
        css: css`
            padding-left: 40px;
        `,
    },
    nameText: {
        color: "primary",
        weight: "bold",
        ellipsis: true,
    },
    addressInfoGridItem: {
        width: 12,
    },
    addressInfoIcon: {
        src: "MapPin",
        color: "primary",
    },
    addressInfoCondensed: {
        wrapper: {
            css: css`
                padding-left: 10px;
                font-style: normal;
            `,
        },
    },
    phoneGridItem: {
        width: 12,
    },
    phoneLink: {
        icon: {
            iconProps: {
                src: Phone,
            },
            position: "left",
        },
        typographyProps: {
            css: css`
                padding-left: 10px;
            `,
        },
    },
    htmlContentGridItem: {
        width: 12,
        css: css`
            padding-left: 40px;
        `,
    },
    googleMapsDirectionLinkGridItem: {
        width: 12,
    },
    visitWebsiteLinkGridItem: {
        width: 12,
    },
};

const styles = dealerInformationStyles;

const DealerInformation: React.FC<Props> = ({ history, locationFinderUrl }) => {
    const { value: dealer } = useContext(DealerStateContext);
    if (!dealer) {
        return null;
    }

    const [locationContentToDisplayModalOpen, setLocationContentToDisplayModalOpen] = React.useState<boolean>(false);
    const [locationContentToDisplay, setLocationContentToDisplay] = React.useState<LocationModel | undefined>(
        undefined,
    );

    const handleOpenContentModal = (dealer: LocationModel) => {
        setLocationContentToDisplayModalOpen(!!dealer.htmlContent);
        setLocationContentToDisplay(dealer);
    };

    const handleContentModalClose = () => {
        setLocationContentToDisplayModalOpen(false);
        setLocationContentToDisplay(undefined);
    };

    const handleBackToResults = () => {
        history.push(locationFinderUrl!);
    };

    return (
        <GridContainer {...styles.container} data-test-selector="dealerInformation" data-test-key={`${dealer.id}`}>
            <GridItem {...styles.selectedDealerDisplayBackToResultsGridItem}>
                <Link onClick={handleBackToResults} {...styles.selectedDealerDisplayBackToResultsLink}>
                    {translate("Back to Results")}
                </Link>
            </GridItem>
            <GridItem {...styles.nameGridItem}>
                <Typography {...styles.nameText}>{dealer.name}</Typography>
            </GridItem>
            <GridItem {...styles.addressInfoGridItem}>
                <Icon {...styles.addressInfoIcon} />
                <AddressInfoCondensedDisplay {...dealer} extendedStyles={styles.addressInfoCondensed} />
            </GridItem>
            {dealer.phone && (
                <GridItem {...styles.phoneGridItem}>
                    {/* <Phone /> */}
                    <Link href={`tel:${dealer.phone}`} {...styles.phoneLink} target="_blank">
                        {dealer.phone}
                    </Link>
                </GridItem>
            )}
            {(dealer as any)?.properties?.fax && (
                <GridItem {...styles.phoneGridItem}>
                    <Link href={`tel:${(dealer as any)?.properties?.fax}`} {...styles.phoneLink} target="_blank">
                        {(dealer as any)?.properties?.fax}
                    </Link>
                </GridItem>
            )}
            <GridItem {...styles.hoursContentGridItem}>
                {(dealer as any)?.properties?.hours && (
                    <LocationContentLink location={dealer} onOpenLocationContent={handleOpenContentModal} />
                )}
            </GridItem>
            <GridItem {...styles.googleMapsDirectionLinkGridItem}>
                <GoogleMapsDirectionLink {...styles.googleMapsDirectionLink} {...dealer} />
            </GridItem>
            {dealer.webSiteUrl && (
                <GridItem {...styles.visitWebsiteLinkGridItem}>
                    <Link href={dealer.webSiteUrl} target="_blank" {...styles.visitWebsiteLink}>
                        {translate("Visit Website")}
                    </Link>
                </GridItem>
            )}
            {dealer.htmlContent && (
                <>
                    <GridItem {...styles.htmlContentGridItem}>
                        <StyledWrapper {...styles.container} data-test-selector="dealerInformation_htmlContent">
                            {parse(transformContentHtml(dealer.htmlContent), parserOptions)}
                        </StyledWrapper>
                    </GridItem>
                </>
            )}
            <Modal
                {...styles.locationContentModal}
                headline={translate("Hours")}
                isOpen={locationContentToDisplayModalOpen}
                handleClose={handleContentModalClose}
            >
                <StyledWrapper>
                    {parse((locationContentToDisplay as any)?.properties?.hours || "", parserOptions)}
                </StyledWrapper>
            </Modal>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(DealerInformation)),
    definition: {
        allowedContexts: ["DealerDetailsPage"],
        group: "Dealer Details",
    },
};

export default widgetModule;
